import React from 'react';
import { ArrowRight, BookOpen, GraduationCap } from 'lucide-react';
import { cn } from '@/lib/utils';
import WhiteLabel from '../../../shared/WhiteLabel';

// Scoped form-control reset for the auth surface.
// Tailwind preflight is disabled globally (AntD compatibility), so raw <input>/<button>
// keep UA chrome. :where() gives the rules zero specificity so Tailwind utilities still win.
const AUTH_SHELL_RESET_CSS = `
:where(.auth-shell) input,
:where(.auth-shell) textarea {
  appearance: none;
  margin: 0;
  border-style: solid;
  outline: none;
  font-family: inherit;
}
:where(.auth-shell) button {
  appearance: none;
  border: 0;
  background: transparent;
  padding: 0;
  margin: 0;
  cursor: pointer;
  font-family: inherit;
}
`;

// Branded half-page panel shows on any *.mediajel.io host (prod + staging, e.g.
// production.mediajel.io / staging.mediajel.io) and mediajel.io itself, plus the
// `dojo.mediajel.ninja` dev environment and `localhost` so Mediajel devs see the
// branded default while working on the product.
// Whitelabel-partner devs can flip to the generic view with `?branded=0`.
// `dojo` is matched exactly (not `.endsWith('.mediajel.ninja')`) because the
// `.ninja` domain also hosts backend services (collector/tags/graphql) we must
// not brand. endsWith form prevents `mediajel.io.evil.com` false-positives.
const isMediajelHost = (h) =>
  h === 'mediajel.io' ||
  h.endsWith('.mediajel.io') ||
  h === 'dojo.mediajel.ninja' ||
  h === 'localhost';

// Resolve branded vs generic. Priority:
//   1. explicit `isMjd` prop (any boolean) wins
//   2. `?branded=1` / `?branded=0` query-param override (dev/QA escape hatch — harmless in prod)
//   3. hostname rule (default behavior)
const resolveIsMjd = (override) => {
  if (override !== undefined) return override;
  const forced = new URLSearchParams(window.location.search).get('branded');
  if (forced === '1') return true;
  if (forced === '0') return false;
  return isMediajelHost(window.location.hostname);
};

const AuthShell = ({
  title,
  subtitle,
  isMjd: isMjdOverride,
  onLogoClick,
  children,
}) => {
  const isMjd = resolveIsMjd(isMjdOverride);
  const logoSrc = WhiteLabel();

  return (
    <div
      className={cn(
        'auth-shell grid min-h-screen grid-cols-1 bg-white text-slate-900 antialiased transition-[grid-template-columns] duration-300 ease-out',
        isMjd && 'lg:grid-cols-2'
      )}
      data-mjd={isMjd ? 'true' : 'false'}
      style={{
        fontFamily:
          "'Plus Jakarta Sans', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
        lineHeight: 1.5,
      }}
    >
      <style>{AUTH_SHELL_RESET_CSS}</style>
      <section className="flex min-h-screen flex-col p-6 sm:p-8 lg:p-12">
        <div className="mx-auto mb-auto mt-[6vh] w-full max-w-[380px] sm:mt-[10vh]">
          <div className="mb-8 flex justify-center">
            <img
              src={logoSrc}
              onClick={onLogoClick}
              alt="Logo"
              className={cn(
                'block h-24 w-auto sm:h-32 lg:h-36',
                onLogoClick && 'cursor-pointer'
              )}
            />
          </div>

          {(title || subtitle) && (
            <header className="mb-8 text-center">
              {title && (
                <h1 className="m-0 mb-2 text-[28px] font-semibold tracking-tight text-slate-900">
                  {title}
                </h1>
              )}
              {subtitle && (
                <p className="m-0 text-[15px] leading-normal text-slate-500">
                  {subtitle}
                </p>
              )}
            </header>
          )}

          {children}
        </div>
      </section>

      <aside
        aria-hidden={!isMjd}
        className={cn(
          'relative overflow-hidden p-8 text-white transition-all duration-300 ease-out lg:p-10 xl:p-14',
          '[container-type:inline-size]',
          'bg-[linear-gradient(135deg,#3DCFE9_0%,#5B7CF7_55%,#9159E6_100%)]',
          isMjd
            ? 'hidden lg:flex lg:flex-col lg:justify-center translate-x-0 opacity-100'
            : 'hidden'
        )}
      >
        <PlexusBackground />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-black/5 to-black/25" />

        <div className="relative mx-auto w-full max-w-[760px]">
          <h2 className="m-0 mb-6 text-[clamp(32px,7.2cqw,68px)] uppercase leading-[1.05] tracking-tight text-white">
            <span className="block whitespace-nowrap font-light">Compliant advertising</span>
            <span className="block whitespace-nowrap font-semibold">meets verified revenue</span>
          </h2>

          <p className="m-0 mb-10 text-[clamp(17px,2.7cqw,24px)] leading-relaxed text-white/80">
            Move beyond ROAS with clear insight into how advertising influences
            revenue and drives incremental growth. From strategy to attribution,
            execute with clarity.
          </p>

          <div className="grid gap-4">
            <ResourceCard
              icon={<BookOpen className="h-5 w-5" />}
              title="Visit the Mediajel Knowledge Base"
              subtitle="Everything you need to build a strong cannabis brand."
              href="https://www.mediajel.com/direct-knowledge-base"
            />
            <ResourceCard
              icon={<GraduationCap className="h-5 w-5" />}
              title="Explore the Mediajel Cannabis Academy"
              subtitle="Courses and playbooks for modern operators."
              href="https://www.mediajel.com/academy"
            />
          </div>
        </div>
      </aside>
    </div>
  );
};

const ResourceCard = ({ icon, title, subtitle, href }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="group flex items-start gap-5 rounded-xl border border-white/15 bg-white/[0.06] p-5 text-inherit no-underline backdrop-blur transition-all duration-200 hover:-translate-y-px hover:border-[#00baff]/50 hover:bg-white/[0.10]"
  >
    <span className="grid h-11 w-11 flex-none place-items-center rounded-lg bg-[#00baff]/15 text-[#00baff]">
      {icon}
    </span>
    <span className="flex-1">
      <p className="m-0 mb-1 text-[17px] font-semibold leading-snug text-white">{title}</p>
      <p className="m-0 text-[15px] leading-snug text-white/70">{subtitle}</p>
    </span>
    <ArrowRight className="ml-auto h-5 w-5 self-center text-white/40 transition-all duration-150 group-hover:translate-x-0.5 group-hover:text-[#00baff]" />
  </a>
);

// Resolution-independent decorative network. ViewBox 1920×1080, scaled with
// preserveAspectRatio="xMidYMid slice" so it always covers the panel cleanly.
// Hub flag draws a soft halo behind the larger nodes.
const PLEXUS_NODES = [
  [1500, 540, 7, true],   // 0  right hub
  [1620, 460, 4, false],  // 1
  [1720, 600, 4, false],  // 2
  [1420, 600, 4, false],  // 3
  [1380, 440, 4, false],  // 4
  [1820, 540, 5, false],  // 5
  [1760, 720, 4, false],  // 6
  [1560, 380, 3, false],  // 7
  [940, 540, 6, true],    // 8  center hub
  [1080, 480, 4, false],  // 9
  [820, 600, 4, false],   // 10
  [880, 420, 3, false],   // 11
  [1080, 660, 4, false],  // 12
  [320, 720, 7, true],    // 13 left-bottom hub
  [220, 820, 4, false],   // 14
  [460, 800, 4, false],   // 15
  [180, 600, 4, false],   // 16
  [420, 620, 4, false],   // 17
  [540, 200, 3, false],   // 18 sparse top
  [1080, 160, 4, false],  // 19
  [1400, 260, 4, false],  // 20
  [720, 320, 3, false],   // 21
  [1700, 120, 4, false],  // 22
  [640, 920, 3, false],   // 23 sparse bottom
  [1240, 880, 4, false],  // 24
  [1820, 880, 4, false],  // 25
  [60, 280, 3, false],    // 26 edges
  [60, 980, 3, false],    // 27
  [1880, 200, 3, false],  // 28
];

const PLEXUS_LINES = [
  [0, 1], [0, 2], [0, 3], [0, 4], [0, 5], [1, 2], [1, 4], [1, 7],
  [2, 5], [2, 6], [3, 6], [3, 12], [4, 7], [5, 6], [5, 28],
  [8, 9], [8, 10], [8, 11], [8, 12], [9, 11], [9, 12], [10, 12], [10, 17],
  [13, 14], [13, 15], [13, 16], [13, 17], [14, 15], [14, 16], [15, 17],
  [16, 17], [15, 23], [18, 21], [18, 19], [19, 20], [19, 21], [20, 22],
  [20, 4], [23, 24], [24, 25], [24, 12], [25, 6],
  [8, 20], [3, 9], [17, 21], [16, 26], [27, 14], [28, 22],
];

const PlexusBackground = () => (
  <svg
    aria-hidden="true"
    viewBox="0 0 1920 1080"
    preserveAspectRatio="xMidYMid slice"
    className="pointer-events-none absolute inset-0 h-full w-full"
  >
    <g stroke="rgba(255,255,255,0.20)" strokeWidth="1" fill="none">
      {PLEXUS_LINES.map(([a, b], i) => (
        <line
          key={i}
          x1={PLEXUS_NODES[a][0]}
          y1={PLEXUS_NODES[a][1]}
          x2={PLEXUS_NODES[b][0]}
          y2={PLEXUS_NODES[b][1]}
        />
      ))}
    </g>
    <g>
      {PLEXUS_NODES.map(([x, y, r, hub], i) => (
        <React.Fragment key={i}>
          {hub && (
            <circle cx={x} cy={y} r={r * 2.8} fill="rgba(255,255,255,0.10)" />
          )}
          <circle cx={x} cy={y} r={r} fill="rgba(255,255,255,0.65)" />
        </React.Fragment>
      ))}
    </g>
  </svg>
);

export default AuthShell;
